import {VNode} from 'snabbdom';
import {AttrsSchema, Component, h} from 'panel';
import moment from 'moment';

import {InputComponent} from 'common/components/input/index';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {ScrollingCalendar} from 'common/components/scrolling-calendar/index';
import {CalendarDay, ScrollingCalendarDateSelection} from 'common/components/scrolling-calendar/util';
import {BannerSize, BannerTheme, BannerType} from 'common/components/banner/types';
import {TIMEZONES} from 'common/constants';
import {getReportName} from 'common/report/util';
import {pluralize} from 'common/util/string';
import {formatDate, getCurrentMoment, parseDate, parseDateWithTimeZone} from 'common/util/date';
import {emitTrackEvent, KEY_VALUES, scrollIntoViewIfNeeded} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {CALENDAR_DATE_FORMAT} from 'common/widgets/date-range-picker/models/date-range';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DateRangeType, DisplayContext, RelativeDate, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {TextAlign} from 'common/components/input/types';
import {NO_MAX_DATE_VALUE} from 'common/components/calendar/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStoreAndFetchOnce} from 'common/util/component-helpers';
import {trackUpsellEvent} from 'common/report/util/tracking-helpers';

import {
  DatePickerMode,
  generateDateRangeTrackingProps,
  getBetweenDateRangeAttributes,
  getCalendarSelection,
  getDatePickerMode,
  getInTheLastDateRangeAttributes,
  getOnDateRangeAttributes,
  getRelativeOffset,
  getRelativeWindowUnit,
  getRelativeWindowValue,
  getSimilarDateRangeForPickerMode,
  getSinceDateRangeAttributes,
  isMaxHistoryFinite,
} from './util';
import css from './index.inline.styl';
import template from './index.jade';

interface DatePickerInputState {
  fromDateText: string;
  fromDate: null | moment.Moment;
  toDateText: string;
  toDate: null | moment.Moment;
  relativeWindowValue: null | number;
  relativeWindowUnit: TimeUnit;
  relativeOffsetValue: null | number;
  showEndingField: boolean;
}

interface DateRangePickerState extends DatePickerInputState {
  pickerMode: DatePickerMode;
  selectedPresetIdx: number;
  showCustomControls: boolean;
}

interface DateRangePickerAttrs {
  'date-range': Partial<DateRange>;
  'date-picker-modes-options': DatePickerMode[];
  'display-context': DisplayContext;
  'single-picker-mode': boolean;
  'max-history-days': number;
  'project-as-of-date': string;
  'tracked-use-case': string;
  'force-hide-preset-menu': boolean;
  'force-hide-calendar-preview': boolean;
  'disable-rolling': boolean;
}

// TODO: add upsell UI based on MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT
export class DateRangePicker extends Component<DateRangePickerState, DateRangePickerAttrs> {
  // store date range attributes instead of DateRange instances in selectedDateRanges
  // because the appropriate DateRange child class is determined by the "display-context" component attribute,
  // so we want to delay instance construction until necessary, to avoid widespread reconstruction when "display-context" changes.
  private selectedDateRanges: Map<DatePickerMode, Partial<DateRange>> = new Map<DatePickerMode, Partial<DateRange>>();
  private hoverIsoDate: null | string = null;
  private calendarElement: null | ScrollingCalendar = null;
  // set of timestamps for dates that are disabled because they occur after the "project as of date" but occur before today
  // note: we store the numeric timestamp in the Set because it should be quickest to compare, and we have to compare values on every date cell in <mp-scrolling-calendar> on every render
  private projectAsOfDateDisabledTimestamps: Set<number> = new Set<number>();

  // see deferUntilFrameAfterMouseupOrNewFocus()
  private mouseupOrNewFocusListener: null | EventListener = null;

  // whether the "from date" or "to date" input was recently selected
  // this determines whether which date input should be considered "active" whenever a user clicks a calendar date cell even after the input has lost focus
  // note: they are public so tests can access them
  public fromDateInputRecentlySelected = false;
  public toDateInputRecentlySelected = false;

  static get attrsSchema(): AttrsSchema<DateRangePickerAttrs> {
    return {
      ...super.attrsSchema,
      'date-range': {type: `json`, default: null, description: `Attributes of a valid DateRange instance`},
      'disable-rolling': `boolean`,
      'display-context': {type: `string`, default: DisplayContext.FullLength},
      'max-history-days': {type: `number`, default: 0, description: `max days time menu can query into data history`},
      'single-picker-mode': {type: `boolean`, description: `if set, switching between date picker modes is disabled`},
      'date-picker-modes-options': {
        type: `json`,
        description: `if set, overrides the list of supported date picker modes`,
        default: [DatePickerMode.Between, DatePickerMode.Since, DatePickerMode.InTheLast],
      },
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'tracked-use-case': {
        type: `string`,
        description: `For tracking purposes only. Unlike "display-context" which represents a unique configuration that may be reused in multiple places, this attribute directly identifies semantic use cases for distinguishing which parts of the product this component is used in.`,
      },
      // TODO: if/when the date picker prototype that force-hides the preset menu is finalized, remove the preset menu from this widget
      'force-hide-preset-menu': {
        type: `boolean`,
        description: `Always hide the side menu that contains a list of presets`,
      },
      'force-hide-calendar-preview': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        pickerMode: DatePickerMode.Between,
        selectedPresetIdx: -1,
        showCustomControls: false,
        showEndingField: false,

        // input state
        fromDateText: ``,
        fromDate: null,
        toDateText: ``,
        toDate: null,
        relativeWindowValue: null,
        relativeWindowUnit: TimeUnit.Day,
        relativeOffsetValue: null,
      },

      helpers: {
        ...super.config.helpers,
        DatePickerMode,
        TextAlign,
        BannerSize,
        BannerTheme,
        BannerType,

        shouldHideCalendarPreview: () =>
          this.attr(`force-hide-calendar-preview`) ||
          this.getSelectedDateRange(DatePickerMode.InTheLast)?.window?.unit === TimeUnit.Hour,
        canUseRolling: () => {
          if (this.attr(`disable-rolling`)) {
            return false;
          }

          const dateRangeClass = this.getDateRangeClass();
          return new dateRangeClass().canUseRolling();
        },
        getPresetMenuSections: (): Section[] => {
          const dateRangeClass = this.getDateRangeClass();
          const presetItems: SectionItem[] = dateRangeClass.getPresetOptions().map((option, idx: number) => {
            const dateRange = new dateRangeClass(option.value);
            if (!dateRange.isValid()) {
              throw new Error(`"${option.label}" preset is invalid`);
            }
            const maxHistoryDays = this.attr(`max-history-days`);
            const isLimited = isMaxHistoryFinite(maxHistoryDays) && !dateRange.isValid(maxHistoryDays);
            return {
              isSelected: this.state.selectedPresetIdx === idx,
              label: option.label,
              value: option.value,
              isDisabled: isLimited,
              tooltip: isLimited
                ? {
                    text: `Your current plan supports ${maxHistoryDays} days of data. Upgrade your plan to query more data.`,
                  }
                : undefined,
            };
          });
          return [{items: presetItems}];
        },
        getPickerModeToggleOptions: (): Section[] => {
          const dateRangeClass = this.getDateRangeClass();
          const validRangeTypes = new Set<DateRangeType>(new dateRangeClass().getDateRangeTypeOptions());

          const pickerModeItems: SectionItem[] = [
            [DatePickerMode.Between, `Fixed`],
            [DatePickerMode.On, `On`],
            [DatePickerMode.Since, `Since`],
            [DatePickerMode.InTheLast, `Last`],
          ]
            .filter(([pickerMode]: [DatePickerMode]) => {
              if (!this.attr(`date-picker-modes-options`).includes(pickerMode)) {
                return false;
              }

              switch (pickerMode) {
                case DatePickerMode.InTheLast: {
                  return validRangeTypes.has(DateRangeType.RelativeAfter);
                }
                case DatePickerMode.Between: {
                  return validRangeTypes.has(DateRangeType.Between);
                }
                case DatePickerMode.Since: {
                  return validRangeTypes.has(DateRangeType.Since);
                }
                case DatePickerMode.On: {
                  return validRangeTypes.has(DateRangeType.On);
                }
                default: {
                  const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
                  throw new Error(`unknown picker mode ${unreachable}`);
                }
              }
            })
            .map(([pickerMode, label]: [DatePickerMode, string]) => ({label, value: pickerMode}));
          return pickerModeItems;
        },
        getTodayIso: (): string => getCurrentMoment({localized: true}).toISOString(),
        getTodayDateString: (): string => {
          const queryTimeZone = currentProjectStore.value?.timezone;
          return getCurrentMoment({timezone: queryTimeZone}).format(`YYYY-MM-DDThh:mm:ss`);
        },
        getMaximumDateIso: (): string => {
          const maxHistoryDays = this.attr(`max-history-days`);
          return isMaxHistoryFinite(maxHistoryDays)
            ? this.getProjectAsOfDate().subtract(maxHistoryDays, `day`).toISOString()
            : ``;
        },
        getDisabledStartIso: (): string => {
          const dateRange = new (this.getDateRangeClass())();
          const queryTimeZone = currentProjectStore.value?.timezone;
          const projectAsOfDate = this.getProjectAsOfDate(queryTimeZone).format(`YYYY-MM-DDThh:mm:ss`);
          return dateRange.getCalendarMaxDate() === NO_MAX_DATE_VALUE ? `` : projectAsOfDate;
        },
        getTimeUnitLabel: (unit: TimeUnit, value: number): string => pluralize(unit, value),
        getStartOfWeek: (): number => {
          const weekStartDaySetting = currentProjectStore.value?.timePeriodSettings?.weekStartDay ?? 0;
          // adjust Setting's weekday (which starts on Monday = 0) to <mp-scrolling-calendar>'s weekday (which starts on Sunday = 0)
          return (weekStartDaySetting + 1) % 7;
        },

        // --- calendar
        getCalendarSelection: (): null | ScrollingCalendarDateSelection => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          const queryTimeZone = currentProjectStore.value?.timezone;

          if (selectedDateRange) {
            return getCalendarSelection(selectedDateRange, this.getProjectAsOfDate(queryTimeZone), queryTimeZone);
          }
          return null;
        },
        getCalendarPreviewSelection: (): null | ScrollingCalendarDateSelection => {
          if (this.shouldShowCalendarPreviewSelection(this.state.pickerMode)) {
            const queryTimeZone = currentProjectStore.value?.timezone;

            let hoverDate = this.hoverIsoDate && moment(this.hoverIsoDate, moment.ISO_8601);
            if (this.hoverIsoDate) {
              hoverDate = parseDateWithTimeZone(hoverDate.format(`YYYY-MM-DD`), queryTimeZone);
            }
            const hoverDateRange = this.getCalendarHoverDateRange(this.state.pickerMode, hoverDate);
            if (hoverDateRange) {
              const selection = getCalendarSelection(
                hoverDateRange,
                this.getProjectAsOfDate(queryTimeZone),
                queryTimeZone,
              );
              if (selection) {
                // This is a visual hack that hides the blue highlight circle that overlaps the hover circle.
                //
                // Without this hack, <mp-scrolling-calendar> would render a hover circle on top of the blue highlight circle,
                // so moving your mouse rapidly across dates can sometimes show both the hover circle and the blue highlight circle
                // visually blended together as the browser tries to render one after the other.
                const overrideHighlightStart = hoverDate?.isSame(moment(selection.start, moment.ISO_8601), `day`);
                const overrideHighlightEnd = hoverDate?.isSame(moment(selection.end, moment.ISO_8601), `day`);
                return {
                  ...selection,
                  ...(overrideHighlightStart && {highlightStart: false}),
                  ...(overrideHighlightEnd && {highlightEnd: false}),
                };
              }
            }
          }
          return null;
        },
        handleCalendarDateClick: (ev: CustomEvent): void => {
          const queryTimeZone = currentProjectStore.value?.timezone;

          let clickedDate = moment(ev.detail.date, moment.ISO_8601);
          if (ev.detail.date) {
            clickedDate = parseDateWithTimeZone(clickedDate.format(`YYYY-MM-DD`), queryTimeZone);
          }
          const {pickerMode} = this.state;

          if (pickerMode === DatePickerMode.Between) {
            if (this.fromDateInputRecentlySelected) {
              const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
                ...this.state,
                fromDate: clickedDate,
              });
              this.selectedDateRanges.set(this.state.pickerMode, replacement);

              this.update({
                fromDateText: clickedDate.format(`MMM D, YYYY`),
                fromDate: clickedDate,
                selectedPresetIdx: -1,
              });
              return;
            }
            if (this.toDateInputRecentlySelected) {
              const replacement = this.getDateRangeFromInputs(pickerMode, {
                ...this.state,
                toDate: clickedDate,
              });
              this.selectedDateRanges.set(pickerMode, replacement);

              this.update({
                toDateText: clickedDate.format(`MMM D, YYYY`),
                toDate: clickedDate,
                selectedPresetIdx: -1,
              });
              return;
            }
          }

          const hoverDateRange = this.getCalendarHoverDateRange(pickerMode, clickedDate);
          if (hoverDateRange) {
            this.selectedDateRanges.set(pickerMode, hoverDateRange);
            this.update({
              ...this.getInputStateFromDateRange(this.getSelectedDateRange(pickerMode)),
              selectedPresetIdx: -1,
            });
          } else if (pickerMode === DatePickerMode.Between) {
            this.selectedDateRanges.set(pickerMode, null);
            this.update({
              fromDateText: clickedDate.format(`MMM D, YYYY`),
              fromDate: clickedDate,
              toDateText: ``,
              toDate: null,
              selectedPresetIdx: -1,
            });
          }
        },
        handleCalendarDateMouseEnter: (ev: CustomEvent): void => {
          this.hoverIsoDate = ev.detail.date;
          this.update();
        },
        handleCalendarDateMouseLeave: (ev: CustomEvent): void => {
          if (this.hoverIsoDate === ev.detail.date) {
            this.hoverIsoDate = null;
            this.update();
          }
        },
        handleCalendarElementInsert: ({elm}: {elm: ScrollingCalendar}) => {
          this.calendarElement = elm;
        },
        handleCalendarElementRemove: (_, removeCallback) => {
          this.calendarElement = null;
          removeCallback();
        },

        // --- window unit
        getWindowUnitMenuSections: (): Section[] => {
          const dateRangeClass = this.getDateRangeClass();
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);

          const items = (selectedDateRange || new dateRangeClass()).getTimeUnitOptions().map(
            (value: TimeUnit): SectionItem => ({
              isSelected: this.state.relativeWindowUnit === value,
              label: this.helpers.getTimeUnitLabel(value, this.state.relativeWindowValue || 0),
              value,
            }),
          );

          return [{items}];
        },
        handleInTheLastWindowUnitChange: (ev: CustomEvent): void => {
          const windowUnit: TimeUnit = ev.detail.item.value;

          const additionalInputChanges: Partial<DatePickerInputState> = {};
          // fallback to 1 for window value because zero-value windows are technically always in "Days" (because they are converted to "today")
          if (windowUnit !== TimeUnit.Day) {
            additionalInputChanges.relativeWindowValue = this.state.relativeWindowValue || 1;
          }

          // update selected date immediately
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            ...additionalInputChanges,
            relativeWindowUnit: windowUnit,
          });
          this.selectedDateRanges.set(this.state.pickerMode, replacement);

          // refresh input
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          this.update({
            ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),

            // make sure user-selected window unit overrides the state from additionalInputState because
            // zero-value windows are technically always in "Days" (because they are converted to "today"), but we want to show the unit that the user just selected
            relativeWindowUnit: windowUnit,
            selectedPresetIdx: -1,
          });
          if (selectedDateRange) {
            const selection = getCalendarSelection(selectedDateRange, this.getProjectAsOfDate());
            if (selection) {
              // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
              this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
            }
          }
        },

        // --- window value
        getSelectedWindowValue: (): number => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            return getRelativeWindowValue(selectedDateRange, this.getProjectAsOfDate());
          }
          return null;
        },
        handleInTheLastWindowValueInput: (ev: InputEvent): void => {
          const target: any = ev.target;
          if (target.value) {
            const inputValue: number = parseInt(target.value, 10);

            // update selected date immediately
            const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
              ...this.state,
              relativeWindowValue: inputValue,
            });
            this.selectedDateRanges.set(this.state.pickerMode, replacement);

            // refresh input with valid value
            const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
            this.update({
              relativeWindowValue: inputValue,
              selectedPresetIdx: -1,
              ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),
            });
            if (selectedDateRange) {
              const selection = getCalendarSelection(selectedDateRange, this.getProjectAsOfDate());
              if (selection) {
                // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
                this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
              }
            }
          }
        },

        // --- window offset
        getSelectedOffset: (): RelativeDate => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            return getRelativeOffset(selectedDateRange, this.getProjectAsOfDate());
          }
          return {unit: TimeUnit.Day, value: 0};
        },
        handleInTheLastOffsetValueInput: (ev: InputEvent): void => {
          const target: any = ev.target;
          const inputValue: number = parseInt(target.value, 10);

          // update selected date immediately
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            relativeOffsetValue: inputValue,
          });
          this.selectedDateRanges.set(this.state.pickerMode, replacement);

          // refresh input with valid value
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          this.update({
            relativeOffsetValue: inputValue,
            selectedPresetIdx: -1,
            ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),
          });
          if (selectedDateRange) {
            const queryTimeZone = currentProjectStore.value?.timezone;

            const selection = getCalendarSelection(
              selectedDateRange,
              this.getProjectAsOfDate(queryTimeZone),
              queryTimeZone,
            );
            if (selection) {
              // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
              this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
            }
          }
        },

        handleInTheLastWindowAndOffsetValueBlur: (): void => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            this.update(this.getInputStateFromDateRange(selectedDateRange));
          }
        },

        handleShowEndingClick: (): void => {
          this.update({showEndingField: true});
          window.requestAnimationFrame(() => {
            const input: InputComponent = this.el.querySelector(`mp-input.relative-offset`);
            input.focus();
          });
        },
        handleHideEndingClick: (): void => {
          // update selected date immediately
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            relativeOffsetValue: 0,
          });
          this.selectedDateRanges.set(this.state.pickerMode, replacement);

          // refresh input with valid value
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          this.update({
            relativeOffsetValue: 0,
            selectedPresetIdx: -1,
            showEndingField: false,
            ...(selectedDateRange && this.getInputStateFromDateRange(selectedDateRange)),
          });
          if (selectedDateRange) {
            const selection = getCalendarSelection(selectedDateRange, this.getProjectAsOfDate());
            if (selection) {
              // selection can be null if selectedDateRange is invalid (eg. for absurdly large month windows that reaches before the epoch)
              this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
            }
          }
        },

        // --- absolute "from" and "to" dates
        handleFromDateInput: (ev: InputEvent): void => {
          const target: any = ev.target;

          const fromDateText: string = target.value;
          const parsedDate: null | Date = parseDate(fromDateText);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            fromDate: parsedDate && moment(parsedDate),
          });
          this.selectedDateRanges.set(this.state.pickerMode, replacement);
          this.update({
            fromDateText,
            fromDate: parsedDate && moment(parsedDate),
            selectedPresetIdx: -1,
          });

          const selection = this.helpers.getCalendarSelection();
          if (selection) {
            this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
          } else if (parsedDate) {
            this.calendarElement.scrollDateIntoView(moment(parsedDate), /* preferNearTop */ true);
          }
        },
        handleToDateInput: (ev: InputEvent): void => {
          const target: any = ev.target;

          const toDateText: string = target.value;
          const parsedDate: null | Date = parseDate(toDateText);
          const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
            ...this.state,
            toDate: parsedDate && moment(parsedDate),
          });
          this.selectedDateRanges.set(this.state.pickerMode, replacement);
          this.update({
            toDateText,
            toDate: parsedDate && moment(parsedDate),
            selectedPresetIdx: -1,
          });

          const selection = this.helpers.getCalendarSelection();
          if (selection) {
            this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ false);
          } else if (parsedDate) {
            this.calendarElement.scrollDateIntoView(moment(parsedDate), /* preferNearTop */ false);
          }
        },
        handleFromDateArrowKeys: (ev: KeyboardEvent): void => {
          if (this.state.fromDate) {
            const newDate: null | moment.Moment = (() => {
              switch (ev.key) {
                case KEY_VALUES.ARROW_UP: {
                  return this.state.fromDate.clone().add(1, `day`);
                }
                case KEY_VALUES.ARROW_DOWN: {
                  return this.state.fromDate.clone().subtract(1, `day`);
                }
              }
              return null;
            })();

            if (newDate) {
              const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
                ...this.state,
                fromDate: newDate,
              });
              this.selectedDateRanges.set(this.state.pickerMode, replacement);
              this.update({
                fromDateText: formatDate(newDate.toDate()),
                fromDate: newDate,
                selectedPresetIdx: -1,
              });

              const selection = this.helpers.getCalendarSelection();
              if (selection) {
                this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ true);
              } else {
                this.calendarElement.scrollDateIntoView(newDate, /* preferNearTop */ true);
              }
              ev.preventDefault();
            }
          }
        },
        handleToDateArrowKeys: (ev: KeyboardEvent): void => {
          if (this.state.toDate) {
            const newDate: null | moment.Moment = (() => {
              switch (ev.key) {
                case KEY_VALUES.ARROW_UP: {
                  return this.state.toDate.clone().add(1, `day`);
                }
                case KEY_VALUES.ARROW_DOWN: {
                  return this.state.toDate.clone().subtract(1, `day`);
                }
              }
              return null;
            })();

            if (newDate) {
              const replacement = this.getDateRangeFromInputs(this.state.pickerMode, {
                ...this.state,
                toDate: newDate,
              });
              this.selectedDateRanges.set(this.state.pickerMode, replacement);
              this.update({
                toDateText: formatDate(newDate.toDate()),
                toDate: newDate,
                selectedPresetIdx: -1,
              });

              const selection = this.helpers.getCalendarSelection();
              if (selection) {
                this.calendarElement.scrollDateSelectionIntoView(selection, /* preferShowStart */ false);
              } else {
                this.calendarElement.scrollDateIntoView(newDate, /* preferNearTop */ false);
              }
              ev.preventDefault();
            }
          }
        },

        // --- presets and modes
        handleClickPreset: (ev: CustomEvent) => {
          const item: SectionItem = ev.detail.item;
          const dateRangeClass = this.getDateRangeClass();
          const selectedPreset = new dateRangeClass(item.value);
          this.dispatchEvent(new CustomEvent(`apply`, {detail: {dateRange: selectedPreset}}));

          this.track(`[mp-query-builder] Select date preset`, {
            ...this.getDateRangeTrackingProps(selectedPreset),
            'Preset name': item.label,
            'Preset source': `mixpanel`, // TODO: move to an enum if/when there are additional sources for presets (eg. user defined presets)
          });
        },
        handleClickCustom: () => {
          this.update({
            showCustomControls: true,
          });
        },
        handleClickPickerMode: (ev: CustomEvent) => {
          const newPickerMode: DatePickerMode = ev.detail.selected;
          const oldPickerMode = this.state.pickerMode;

          if (this.state.selectedPresetIdx > -1 || newPickerMode !== oldPickerMode) {
            // attempt to "transition" with a similar date range if possible
            let similarDate;
            if (newPickerMode === DatePickerMode.Since) {
              similarDate = this.getDateRangeFromInputs(newPickerMode, this.state);
            } else {
              const oldSelectedDateRange = this.getSelectedDateRange(oldPickerMode);
              const queryTimeZone = currentProjectStore.value?.timezone;
              similarDate =
                oldSelectedDateRange &&
                getSimilarDateRangeForPickerMode(
                  oldSelectedDateRange,
                  this.getProjectAsOfDate(queryTimeZone),
                  newPickerMode,
                  queryTimeZone,
                );
            }

            if (similarDate) {
              this.selectedDateRanges.set(newPickerMode, similarDate);
            }

            const newSelectedDateRange = this.getSelectedDateRange(newPickerMode);
            this.update({
              pickerMode: newPickerMode,
              // even if the last selected DateRange for this range type is a preset,
              // deselect presets because the user just explicitly clicked a range type
              selectedPresetIdx: -1,
              ...(newSelectedDateRange && this.getInputStateFromDateRange(newSelectedDateRange)),
            });
          }
        },

        // --- upsell
        shouldShowUpsell: (): boolean => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          if (selectedDateRange) {
            const maxHistoryDays = this.attr(`max-history-days`);
            return isMaxHistoryFinite(maxHistoryDays) && !selectedDateRange.isValid(maxHistoryDays);
          }
          return false;
        },
        handleUpsellBannerInsert: (): void => {
          trackUpsellEvent(`View Upsell`, `date_picker`);
        },
        handleUpsellClick: (): void => {
          trackUpsellEvent(`Click Upsell`, `date_picker`);
        },

        // --- action bar
        handleCancelClick: (): void => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },
        canApply: (): boolean => {
          const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
          const queryTimeZone = currentProjectStore.value?.timezone;

          if (selectedDateRange?.isValid(this.attr(`max-history-days`))) {
            const {from, to} = selectedDateRange.getCalendarDatesAsOf(
              this.getProjectAsOfDate(queryTimeZone).format(CALENDAR_DATE_FORMAT),
            );
            let fromDate = moment(parseDate(from));
            let toDate = moment(parseDate(to));
            // returns `now()` datetime (YYYY-MM-DDThh-mm-ss) in Query Timezone
            let projectAsOfDate = this.getProjectAsOfDate(queryTimeZone);

            // force all three dates to be in browser timezone before doing isSameOrBefore check.
            // this is allowed because we are only interested in comparing `dates`. Thus, it does
            // not matter if the dates are in a certain timezone, they just have to be all in
            projectAsOfDate = moment(projectAsOfDate.format(`YYYY-MM-DD`));
            fromDate = moment(fromDate.format(`YYYY-MM-DD`));
            toDate = moment(toDate.format(`YYYY-MM-DD`));

            const isSameOrBeforeAsOfDate =
              fromDate.isSameOrBefore(projectAsOfDate, `day`) && toDate.isSameOrBefore(projectAsOfDate, `day`);
            const datesCorrectlyOrdered = fromDate.isSameOrBefore(toDate, `day`);
            if (
              selectedDateRange.getCalendarMaxDate() === NO_MAX_DATE_VALUE ||
              (isSameOrBeforeAsOfDate && datesCorrectlyOrdered)
            ) {
              return true;
            }
          }
          return false;
        },
        handleApplyClick: (): void => {
          if (this.helpers.canApply()) {
            const dateRange = this.getSelectedDateRange(this.state.pickerMode);

            this.dispatchEvent(new CustomEvent(`apply`, {detail: {dateRange}}));

            this.track(`[mp-query-builder] Select custom date range`, {
              ...this.getDateRangeTrackingProps(dateRange),
              'Ending days ago (offset)':
                getDatePickerMode(dateRange) === DatePickerMode.InTheLast
                  ? getRelativeOffset(dateRange, this.getProjectAsOfDate())?.value
                  : null,
            });
          }
        },

        renderCalendarDayTooltip: (day: CalendarDay): null | VNode => {
          if (this.projectAsOfDateDisabledTimestamps.size > 0) {
            const dayTimestamp = moment({
              year: day.year,
              month: day.month,
              day: day.isoDayOfMonth,
            }).valueOf();
            if (this.projectAsOfDateDisabledTimestamps.has(dayTimestamp)) {
              const superimportMessage = `Data last imported ${this.getProjectAsOfDate().format(`MMM D, YYYY`)}`;
              return h(`mp-tooltip`, {attrs: {elref: `dateTooltip`, placement: `top`}}, superimportMessage);
            }
          }
          return null;
        },
      },
    };
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    switch (name) {
      case `date-range`: {
        const dateRangeAttrs = JSON.parse(newValue);
        const dateRangeClass = this.getDateRangeClass();
        const dateRange = dateRangeAttrs && new dateRangeClass(dateRangeAttrs);
        if (dateRange) {
          if (!dateRange.isValid()) {
            throw new Error(`"date-range" attribute contains invalid DateRange info`);
          }
          switch (dateRange.type) {
            case DateRangeType.On:
            case DateRangeType.RelativeAfter:
            case DateRangeType.Between:
            case DateRangeType.Since:
              break;
            default:
              throw new Error(`"date-range" attribute contains DateRange info with unsupported DateRangeType`);
          }

          const pickerMode = getDatePickerMode(dateRange);
          this.selectedDateRanges.set(pickerMode, dateRange.getAttributes());
          this.state.pickerMode = pickerMode;

          // super.attributeChangedCallback() naturally calls this.update();
          const selectedDateRange = this.getSelectedDateRange(pickerMode);
          Object.assign(this.state, this.getInputStateFromDateRange(selectedDateRange));
        }
        // super.attributeChangedCallback() naturally calls this.update();
        this.state.selectedPresetIdx = this.getSelectedPresetIdx();
        if (dateRange) {
          this.state.showCustomControls = this.state.selectedPresetIdx === -1;
        }
        break;
      }
      case `max-history-days`: {
        // super.attributeChangedCallback() naturally calls this.update();
        // update selectedPresetIdx here because preset options may change depending on max-history-days
        this.state.selectedPresetIdx = this.getSelectedPresetIdx();
        break;
      }
      case `display-context`: {
        const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
        if (selectedDateRange) {
          // super.attributeChangedCallback() naturally calls this.update();
          Object.assign(this.state, this.getInputStateFromDateRange(selectedDateRange));
        }

        // super.attributeChangedCallback() naturally calls this.update();
        // update selectedPresetIdx here because preset options may change depending on display-context
        this.state.selectedPresetIdx = this.getSelectedPresetIdx();
        break;
      }
    }

    super.attributeChangedCallback(name, oldValue, newValue);

    // run after super.attributeChangedCallback() because "this.getProjectAsOfDate()" relies on this.attr()
    if (name === `project-as-of-date`) {
      this.projectAsOfDateDisabledTimestamps.clear();
      const today = getCurrentMoment({localized: true});
      for (let date = this.getProjectAsOfDate().add(1, `day`); date.isSameOrBefore(today, `day`); date.add(1, `day`)) {
        this.projectAsOfDateDisabledTimestamps.add(date.valueOf());
      }
    }
  }

  public connectedCallback() {
    super.connectedCallback();
    useStoreAndFetchOnce(this, currentProjectStore);
  }

  public disconnectedCallback() {
    this.cleanupMouseupOrNewFocusListener();
    super.disconnectedCallback();
  }

  public customScrollIntoViewIfNeeded() {
    requestAnimationFrame(() => {
      scrollIntoViewIfNeeded(this);
    });
  }

  // Expose these handlers publicly so they can be used directly from tests,
  // important because focus/blur workflows are difficult to test reliably.
  public handleFromDateFocus(): void {
    this.fromDateInputRecentlySelected = true;
  }
  public handleFromDateBlur(): void {
    this.deferUntilFrameAfterMouseupOrNewFocus(() => (this.fromDateInputRecentlySelected = false));
    this.applyAndFormatFromAndToDateInputs(this.state);
  }
  public handleToDateFocus(): void {
    this.toDateInputRecentlySelected = true;
  }
  public handleToDateBlur(): void {
    this.deferUntilFrameAfterMouseupOrNewFocus(() => (this.toDateInputRecentlySelected = false));
    this.applyAndFormatFromAndToDateInputs(this.state);
  }

  private getProjectAsOfDate(timezone?: typeof TIMEZONES[number]): moment.Moment {
    const explicitAsOfDate = this.attr(`project-as-of-date`);
    if (explicitAsOfDate) {
      const parsedDate = parseDate(explicitAsOfDate);
      if (!parsedDate) {
        throw new Error(`"project-as-of-date" attribute does not contain a valid date string`);
      }
      return moment(parsedDate);
    }
    return getCurrentMoment({localized: true, timezone});
  }

  private getSelectedDateRange(pickerMode: DatePickerMode): null | DateRange {
    if (this.selectedDateRanges.has(pickerMode)) {
      const dateRangeAttrs = this.selectedDateRanges.get(pickerMode);
      if (dateRangeAttrs) {
        const dateRangeClass = this.getDateRangeClass();
        return new dateRangeClass(this.selectedDateRanges.get(pickerMode));
      }
    }
    return null;
  }

  private getSelectedPresetIdx(): number {
    const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
    if (selectedDateRange) {
      const presetMenuItems = this.helpers.getPresetMenuSections()[0].items;
      return presetMenuItems.findIndex((menuItem) => selectedDateRange.equals(menuItem.value));
    }
    return -1;
  }

  private getDateRangeClass(): typeof DateRange {
    const displayContext = this.attr(`display-context`);
    return DATE_RANGE_TYPES[displayContext];
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getDateRangeFromInputs(
    pickerMode: DatePickerMode,
    inputState: Partial<DatePickerInputState>,
  ): null | Partial<DateRange> {
    const {fromDate, toDate, relativeWindowValue, relativeWindowUnit, relativeOffsetValue} = inputState;
    switch (pickerMode) {
      case DatePickerMode.InTheLast: {
        const windowValue = Number.isInteger(relativeWindowValue) ? relativeWindowValue : 0;
        if (windowValue >= 0) {
          const window: RelativeDate = {
            value: windowValue,
            unit: relativeWindowUnit,
          };

          const offsetValue = Number.isInteger(relativeOffsetValue) ? relativeOffsetValue : 0;
          if (offsetValue >= 0) {
            const canBeRolling = window.unit === TimeUnit.Day;
            const offset = canBeRolling
              ? {
                  value: relativeOffsetValue,
                  unit: window.unit,
                }
              : null;

            return getInTheLastDateRangeAttributes(window, offset);
          }
        }
        return null;
      }
      case DatePickerMode.On: {
        if (fromDate) {
          return getBetweenDateRangeAttributes(fromDate, fromDate);
        }
        break;
      }
      case DatePickerMode.Between: {
        if (fromDate && toDate && fromDate.isSameOrBefore(toDate, `day`)) {
          return getBetweenDateRangeAttributes(fromDate, toDate);
        }
        break;
      }
      case DatePickerMode.Since: {
        if (fromDate) {
          return getSinceDateRangeAttributes(fromDate);
        }
        break;
      }
    }
    return null;
  }

  private getInputStateFromDateRange(dateRange: DateRange): null | Partial<DatePickerInputState> {
    const pickerMode = getDatePickerMode(dateRange);
    switch (pickerMode) {
      case DatePickerMode.InTheLast: {
        const relativeOffsetValue = getRelativeOffset(dateRange, this.getProjectAsOfDate())?.value;
        return {
          relativeWindowValue: getRelativeWindowValue(dateRange, this.getProjectAsOfDate()),
          relativeWindowUnit: getRelativeWindowUnit(dateRange),
          relativeOffsetValue,

          // note: if relativeOffsetValue is explicitly zero, don't change showEndingField
          ...(relativeOffsetValue === 0 ? {} : {showEndingField: relativeOffsetValue > 0}),
        };
      }
      case DatePickerMode.On:
      case DatePickerMode.Between:
      case DatePickerMode.Since: {
        const selectedFromDate = moment(parseDate(dateRange.getCalendarFromDate()));
        const selectedToDate = moment(parseDate(dateRange.getCalendarToDate()));

        const defaultFromDate = selectedFromDate || this.state.fromDate;
        const defaultToDate = selectedToDate || this.state.toDate;
        return {
          fromDateText: defaultFromDate ? formatDate(defaultFromDate.toDate()) : ``,
          fromDate: defaultFromDate,
          toDateText: defaultToDate ? formatDate(defaultToDate.toDate()) : ``,
          toDate: defaultToDate,
        };
      }
      default: {
        const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
        throw new Error(`unknown picker mode ${unreachable}`);
      }
    }
  }

  private shouldShowCalendarPreviewSelection(pickerMode: DatePickerMode): boolean {
    switch (pickerMode) {
      case DatePickerMode.InTheLast:
      case DatePickerMode.Since: {
        // if there is an existing selection, just don't show the preview selection to avoid visual overlapping
        return !this.getSelectedDateRange(pickerMode);
      }
      case DatePickerMode.Between: {
        const {fromDate, toDate} = this.state;
        const onlyOneDate = (!fromDate && toDate) || (!toDate && fromDate);
        return !!onlyOneDate;
      }
      default:
        return false;
    }
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getCalendarHoverDateRange(pickerMode: DatePickerMode, hoverDate: null | moment.Moment): null | DateRange {
    const dateRangeClass = this.getDateRangeClass();
    switch (pickerMode) {
      case DatePickerMode.InTheLast: {
        if (hoverDate) {
          const queryTimeZone = currentProjectStore.value?.timezone;
          const dayValue = this.getProjectAsOfDate(queryTimeZone).diff(hoverDate, TimeUnit.Day);
          const offsetValue = Number.isInteger(this.state.relativeOffsetValue) ? this.state.relativeOffsetValue : 0;
          const windowValue = dayValue - offsetValue;
          return new dateRangeClass(
            getInTheLastDateRangeAttributes(
              {value: windowValue >= 0 ? windowValue : dayValue, unit: TimeUnit.Day},
              {value: windowValue >= 0 ? offsetValue : 0, unit: TimeUnit.Day},
            ),
          );
        }
        break;
      }
      case DatePickerMode.Between: {
        const {fromDate, toDate} = this.state;
        const hoverAsOfDate = (!fromDate && toDate) || (!toDate && fromDate);
        if (hoverAsOfDate) {
          return new dateRangeClass(getBetweenDateRangeAttributes(hoverDate || hoverAsOfDate, hoverAsOfDate));
        }
        break;
      }
      case DatePickerMode.On: {
        if (hoverDate) {
          return new dateRangeClass(getOnDateRangeAttributes(hoverDate));
        }
        break;
      }
      case DatePickerMode.Since: {
        if (hoverDate) {
          return new dateRangeClass(getSinceDateRangeAttributes(hoverDate));
        }
        break;
      }
    }
    return null;
  }

  private applyAndFormatFromAndToDateInputs(inputState: Partial<DatePickerInputState>): void {
    const replacement = this.getDateRangeFromInputs(this.state.pickerMode, inputState);
    this.selectedDateRanges.set(this.state.pickerMode, replacement);

    const selectedDateRange = this.getSelectedDateRange(this.state.pickerMode);
    if (selectedDateRange) {
      const newInputState = this.getInputStateFromDateRange(selectedDateRange);
      this.update({
        ...newInputState,
        ...this.getDateRangeFromInputs(this.state.pickerMode, newInputState),
      });
    } else {
      this.update();
    }
  }

  private track(event: string, properties: {[index: string]: any}): void {
    emitTrackEvent(this, {eventName: event, props: properties, cancelable: true});
  }

  private getDateRangeTrackingProps(dateRange: DateRange) {
    return generateDateRangeTrackingProps(
      dateRange,
      getReportName(),
      this.state.pickerMode,
      this.getProjectAsOfDate(),
      this.attr(`tracked-use-case`),
    );
  }

  private cleanupMouseupOrNewFocusListener(): void {
    if (this.mouseupOrNewFocusListener) {
      window.removeEventListener(`mouseup`, this.mouseupOrNewFocusListener);
      window.removeEventListener(`touchend`, this.mouseupOrNewFocusListener);
      this.el.removeEventListener(`focusin`, this.mouseupOrNewFocusListener, true);
    }
    this.mouseupOrNewFocusListener = null;
  }

  private deferUntilFrameAfterMouseupOrNewFocus(deferredAction: () => void): void {
    this.cleanupMouseupOrNewFocusListener();

    this.mouseupOrNewFocusListener = () => {
      this.cleanupMouseupOrNewFocusListener();
      requestAnimationFrame(deferredAction.bind(this));
    };
    window.addEventListener(`mouseup`, this.mouseupOrNewFocusListener);
    window.addEventListener(`touchend`, this.mouseupOrNewFocusListener);
    this.el.addEventListener(`focusin`, this.mouseupOrNewFocusListener, true);
  }
}

export const DateRangePickerEl = defineAndMakeJsxFactory(`mp-date-range-picker-new`, DateRangePicker);
export const DateRangePickerReact = panel2react(`mp-date-range-picker-new`, DateRangePicker);
